import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Trash2, Plus, Minus, Loader2, CheckCircle2, Truck, Lock } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { api, formatErrorDetail } from "@/lib/api";
import { fmtNumber } from "@/lib/prices";
import { toast } from "sonner";

import { SHIPPING_FEE_PIECES, SHIPPING_FREE_THRESHOLD_PIECES } from "@/lib/pieces";
import CheckoutForm from "@/components/shop/CheckoutForm";
import VirementDone from "@/components/shop/VirementDone";
const eur = new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function computeShipping(items) {
  const pieces = items.filter((i) => i.type === "piece");
  if (pieces.length === 0) return { applies: false, cost: 0, subtotal: 0, freeAt: SHIPPING_FREE_THRESHOLD_PIECES };
  const subtotal = pieces.reduce((s, i) => s + (i.prix_ttc || i.prix_ht || 0) * i.qty, 0);
  const cost = subtotal >= SHIPPING_FREE_THRESHOLD_PIECES ? 0 : SHIPPING_FEE_PIECES;
  return { applies: true, cost, subtotal, freeAt: SHIPPING_FREE_THRESHOLD_PIECES };
}

export default function CartSheet() {
  const { items, removeItem, updateQty, open, setOpen, clear } = useCart();
  const [step, setStep] = useState("cart");
  const [form, setForm] = useState({ nom: "", email: "", telephone: "", adresse: "", code_postal: "", ville: "", horaire_contact: "matin", societe: "", departement: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [virement, setVirement] = useState(null);
  const pieces = items.filter((i) => i.type === "piece");
  const others = items.filter((i) => i.type !== "piece");
  const reset = () => { setStep("cart"); setDone(false); setVirement(null); };
  const onPaid = ({ virement: v, devisSent }) => {
    if (!v) return;
    setVirement({ ...v, devisSent });
    clear();
  };

  const totalHT = items.reduce((s, i) => s + (i.prix_ht || 0) * i.qty, 0);
  const subtotalTTC = items.reduce((s, i) => s + (i.prix_ttc || 0) * i.qty, 0);
  const shipping = computeShipping(items);
  const totalTTC = subtotalTTC + shipping.cost;
  const missingForFree = shipping.applies && shipping.cost > 0 ? Math.max(0, shipping.freeAt - shipping.subtotal) : 0;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const cartItems = items.map((i) => ({
        slug: i.slug, type: i.type, nom: i.nom,
        prix: i.prix_ttc || i.prix_ht || 0,
        prix_unite: i.prix_ttc ? "TTC" : "HT",
        qty: i.qty,
      }));
      // Add shipping as a virtual line item if applicable
      if (shipping.applies && shipping.cost > 0) {
        cartItems.push({
          slug: "frais-port-pieces",
          type: "livraison",
          nom: "Frais de port pièces détachées",
          prix: shipping.cost,
          prix_unite: "TTC",
          qty: 1,
        });
      }
      await api.post("/devis-panier", { ...form, items: cartItems });
      setDone(true);
      toast.success("Demande envoyée !");
      clear();
    } catch (err) {
      toast.error(formatErrorDetail(err.response?.data?.detail) || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Sheet
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) reset();
      }}
    >
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0" data-testid="cart-sheet">
        <SheetHeader className="px-6 py-5 border-b border-gray-200">
          <SheetTitle className="font-heading text-2xl text-[#072135]">
            {virement ? "Commande enregistrée" : done ? "Demande envoyée" : step === "cart" ? "Votre panier" : step === "pay" ? "Commander mes pièces" : "Vos coordonnées"}
          </SheetTitle>
        </SheetHeader>

        {virement ? (
          <VirementDone order={virement} devisSent={virement.devisSent} onClose={() => { setOpen(false); reset(); }} />
        ) : step === "pay" && pieces.length > 0 ? (
          <CheckoutForm pieces={pieces} others={others} onBack={() => setStep("cart")} onDone={onPaid} />
        ) : done ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
            <CheckCircle2 size={48} className="text-[#D8B86A] mb-4" />
            <h3 className="font-heading text-xl font-bold text-[#072135] mb-2">Merci !</h3>
            <p className="text-sm text-[#4B5563] mb-6">Votre demande a bien été envoyée. Nous vous recontactons sous 24h.</p>
            <button onClick={() => { setOpen(false); reset(); }} className="lmb-btn-primary">Fermer</button>
          </div>
        ) : items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center text-[#4B5563]">
            <p>Votre panier est vide.</p>
          </div>
        ) : step === "cart" ? (
          <>
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {items.map((it) => (
                <div key={`${it.type}-${it.slug}`} data-testid={`cart-item-${it.slug}`} className="flex gap-3 p-3 border border-gray-200 rounded-sm bg-white">
                  {it.image && (
                    <div className="w-16 h-16 flex-shrink-0 bg-[#F9F9F9] overflow-hidden rounded-sm">
                      <img src={it.image} alt={it.nom} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-mono-tech uppercase tracking-widest text-[#D8B86A]">{it.type}</div>
                    <div className="font-heading font-bold text-[#072135] text-sm leading-tight truncate">{it.nom}</div>
                    <div className="mt-1">
                      {it.prix_ttc != null && (
                        <div className="lmb-price text-base leading-tight">{fmtNumber(it.prix_ttc)} € TTC</div>
                      )}
                      {it.prix_ht != null && (
                        <div className="text-xs text-[#4B5563] font-mono-tech">{fmtNumber(it.prix_ht)} € HT</div>
                      )}
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-1">
                        <button onClick={() => updateQty(it.slug, it.type, it.qty - 1)} className="w-7 h-7 border border-gray-300 rounded-sm flex items-center justify-center hover:bg-gray-50"><Minus size={12} /></button>
                        <span className="w-8 text-center font-mono-tech text-sm font-bold">{it.qty}</span>
                        <button onClick={() => updateQty(it.slug, it.type, it.qty + 1)} className="w-7 h-7 border border-gray-300 rounded-sm flex items-center justify-center hover:bg-gray-50"><Plus size={12} /></button>
                      </div>
                      <button onClick={() => removeItem(it.slug, it.type)} className="text-red-500 hover:text-red-700 p-1" data-testid={`remove-${it.slug}`}><Trash2 size={14} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-gray-200 p-6 space-y-2">
              {shipping.applies && (
                <div className="bg-[#F9F9F9] border border-gray-200 rounded-sm p-3 space-y-1.5" data-testid="shipping-block">
                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#364757]">
                    <Truck size={14} className="text-[#D8B86A]" /> Frais de port pièces détachées
                  </div>
                  <div className="flex justify-between items-baseline text-sm">
                    <span className="text-[#4B5563]">Sous-total pièces</span>
                    <span className="font-mono-tech">{fmtNumber(shipping.subtotal)} € TTC</span>
                  </div>
                  <div className="flex justify-between items-baseline text-sm">
                    <span className="text-[#4B5563]">Livraison</span>
                    {shipping.cost === 0 ? (
                      <span className="font-mono-tech font-bold text-green-600" data-testid="shipping-free">OFFERTE ✓</span>
                    ) : (
                      <span className="font-mono-tech font-bold" data-testid="shipping-cost">{eur.format(shipping.cost)} €</span>
                    )}
                  </div>
                  {missingForFree > 0 && (
                    <div className="text-[11px] text-[#D8B86A] font-bold" data-testid="shipping-hint">
                      Plus que {eur.format(missingForFree)} € pour le port offert !
                    </div>
                  )}
                </div>
              )}
              <div className="flex items-baseline justify-between">
                <span className="text-xs text-[#4B5563] uppercase tracking-widest font-mono-tech">Total HT</span>
                <span className="font-mono-tech text-base font-bold text-[#072135]">{fmtNumber(totalHT)} € HT</span>
              </div>
              <div className="flex items-baseline justify-between">
                <span className="text-sm text-[#4B5563] uppercase tracking-widest font-mono-tech">Total TTC</span>
                <span className="lmb-price text-2xl">{fmtNumber(totalTTC)} € TTC</span>
              </div>
              {pieces.length > 0 ? (
                <>
                  <button data-testid="cart-pay-pieces" onClick={() => setStep("pay")} className="lmb-btn-primary w-full mt-2"><Lock size={14} /> Commander et payer mes pièces</button>
                  <p className="text-[11px] text-[#4B5563] text-center">Carte bancaire · PayPal · Apple Pay · Virement · Retrait atelier</p>
                  {others.length > 0 && (
                    <p className="text-[11px] text-[#B45309]" data-testid="cart-mixed-note">Les accessoires et mini-pelles ne se paient pas en ligne : ils seront envoyés en demande de devis avec votre commande.</p>
                  )}
                </>
              ) : (
                <>
                  <p className="text-[11px] text-[#4B5563]">Montant indicatif. Devis personnalisé établi par notre équipe.</p>
                  <button data-testid="cart-checkout" onClick={() => setStep("form")} className="lmb-btn-primary w-full mt-2">Demander un devis</button>
                </>
              )}
            </div>
          </>
        ) : (
          <form onSubmit={submit} className="flex-1 overflow-y-auto p-6 space-y-3" data-testid="cart-form">
            <input data-testid="cart-nom" required placeholder="Nom complet *" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} className="w-full px-3 py-2.5 border border-gray-300 rounded-sm text-sm" />
            <input data-testid="cart-email" type="email" required placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2.5 border border-gray-300 rounded-sm text-sm" />
            <input data-testid="cart-tel" required placeholder="Téléphone *" value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} className="w-full px-3 py-2.5 border border-gray-300 rounded-sm text-sm" />
            <input data-testid="cart-adresse" required placeholder="Adresse *" value={form.adresse} onChange={(e) => setForm({ ...form, adresse: e.target.value })} className="w-full px-3 py-2.5 border border-gray-300 rounded-sm text-sm" />
            <div className="grid grid-cols-2 gap-2">
              <input data-testid="cart-cp" required placeholder="Code postal *" value={form.code_postal} onChange={(e) => setForm({ ...form, code_postal: e.target.value })} className="px-3 py-2.5 border border-gray-300 rounded-sm text-sm" />
              <input data-testid="cart-ville" required placeholder="Ville *" value={form.ville} onChange={(e) => setForm({ ...form, ville: e.target.value })} className="px-3 py-2.5 border border-gray-300 rounded-sm text-sm" />
            </div>
            <select data-testid="cart-horaire" required value={form.horaire_contact} onChange={(e) => setForm({ ...form, horaire_contact: e.target.value })} className="w-full px-3 py-2.5 border border-gray-300 rounded-sm text-sm bg-white">
              <option value="matin">Contact préféré : Matin (8h–12h)</option>
              <option value="midi">Contact préféré : Midi (12h–14h)</option>
              <option value="apres-midi">Contact préféré : Après-midi (14h–18h)</option>
              <option value="soir">Contact préféré : Soir (18h–20h)</option>
              <option value="indifferent">Contact préféré : Indifférent</option>
            </select>
            <textarea data-testid="cart-message" rows={3} placeholder="Message (optionnel)" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full px-3 py-2.5 border border-gray-300 rounded-sm text-sm resize-none" />
            <div className="bg-[#F9F9F9] border border-gray-200 p-3 rounded-sm">
              <div className="text-xs font-bold uppercase tracking-wider text-[#364757] mb-2">Récapitulatif</div>
              <ul className="space-y-1 text-xs text-[#111111]">
                {items.map((it) => (
                  <li key={`r-${it.slug}-${it.type}`} className="flex justify-between gap-2">
                    <span>{it.qty}× {it.nom}</span>
                    <span className="font-mono-tech">{fmtNumber((it.prix_ttc || it.prix_ht || 0) * it.qty)} €</span>
                  </li>
                ))}
              </ul>
              <div className="mt-2 pt-2 border-t border-gray-300 space-y-1">
                {shipping.applies && shipping.cost > 0 && (
                  <div className="flex justify-between text-xs"><span>Frais de port pièces</span><span className="font-mono-tech font-bold">{eur.format(shipping.cost)} €</span></div>
                )}
                {shipping.applies && shipping.cost === 0 && (
                  <div className="flex justify-between text-xs text-green-600"><span>Frais de port pièces</span><span className="font-mono-tech font-bold">OFFERT ✓</span></div>
                )}
                <div className="flex justify-between text-xs"><span>Total HT</span><span className="font-mono-tech font-bold">{fmtNumber(totalHT)} € HT</span></div>
                <div className="flex justify-between"><span className="font-bold">Total TTC</span><span className="lmb-price">{fmtNumber(totalTTC)} € TTC</span></div>
              </div>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => setStep("cart")} className="px-4 py-2.5 border border-gray-300 rounded-sm text-sm">Retour</button>
              <button data-testid="cart-submit" type="submit" disabled={loading} className="lmb-btn-primary flex-1">
                {loading ? <Loader2 size={14} className="animate-spin" /> : null}
                {loading ? "Envoi…" : "Envoyer la demande"}
              </button>
            </div>
          </form>
        )}
      </SheetContent>
    </Sheet>
  );
}
